import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import RepoDetailsTabs from './RepoDetailsTabs';
import config from '../config/config';

function RepoDetails() {
  const { id: repoName } = useParams();
  const [repoExists, setRepoExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentBranch, setCurrentBranch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [projectInfo, setProjectInfo] = useState([]);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${config.BASE_URL}/repository/exists/${repoName}`);
        setRepoExists(res.data.exists);
        if (res.data.exists) {
          const branchRes = await axios.get(`${config.BASE_URL}/repository/branch/current/${repoName}`);
          if (branchRes.status === 200) setCurrentBranch(branchRes.data.currentBranch);
          const infoRes = await axios.get(`${config.BASE_URL}/projects/${repoName}/info`);
          setProjectInfo(infoRes.data || []);
        } else {
          setProjectInfo([]);
        }
      } catch (err) {
        console.error('Failed to check repo:', err);
        setProjectInfo([]);
      }
    };
    check();
  }, [repoName]);

  useEffect(() => {
    if (repoExists && !currentBranch) {
      axios.get(`${config.BASE_URL}/repository/branches/${repoName}`)
        .then(r => {
          const list = r.data || [];
          const active = list.find(b => b.startsWith('*'));
          setCurrentBranch(active ? active.slice(1).trim() : list[0] || '');
        })
        .catch(() => {});
    }
  }, [repoName, repoExists, currentBranch]);

  const handleCloneRepo = async () => {
    setLoading(true);
    setErrorMessage(''); setSuccessMessage('');
    try {
      await axios.post(`${config.BASE_URL}/repository/clone/INGCDaaS/IngOne/${repoName}`);
      setRepoExists(true);
      setSuccessMessage(`Repository "${repoName}" cloned successfully.`);
    } catch {
      setErrorMessage('Failed to clone the repository. Please try again.');
    }
    setLoading(false);
  };

  const repoSummary = useMemo(() => {
    const unique = (values) => Array.from(new Set((values || []).filter(Boolean)));
    return {
      languages: unique(projectInfo.map((item) => item.Language)),
      managers: unique(projectInfo.map((item) => item.Manager)),
      manifests: unique(projectInfo.map((item) => item.Manifest)),
      projects: unique(projectInfo.map((item) => item.Project)),
      hasMonorepo: projectInfo.some((item) => item.IsParent || (item.Children || []).length > 0),
    };
  }, [projectInfo]);

  return (
    <div className="App-body">

      {/* ── Breadcrumb ── */}
      <div className="ce-breadcrumb">
        <Link to="/">Dashboard</Link>
        <span className="ce-breadcrumb-sep">›</span>
        <Link to="/repos">Projects</Link>
        <span className="ce-breadcrumb-sep">›</span>
        <span className="ce-breadcrumb-current">{repoName}</span>
      </div>

      {/* ── Alerts ── */}
      {errorMessage && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {errorMessage}
          <button type="button" className="btn-close" onClick={() => setErrorMessage('')} aria-label="Close" />
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success alert-dismissible" role="alert">
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage('')} aria-label="Close" />
        </div>
      )}

      {/* ── Repo hero card ── */}
      <div className="rd-hero mb-4">
        <div className="rd-hero-body">

          {/* Left: identity */}
          <div className="rd-identity">
            <div className="rd-repo-name">
              <span className="rd-repo-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3h18v18H3z" opacity=".15" fill="currentColor" stroke="none"/>
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <path d="M7 7h4M7 12h10M7 17h7"/>
                </svg>
              </span>
              <h1 className="rd-repo-title">{repoName}</h1>
              {repoExists
                ? <span className="rd-status-pill rd-status-pill--ok">✓ Available locally</span>
                : <span className="rd-status-pill rd-status-pill--missing">Not cloned</span>}
            </div>

            {repoExists && (
              <div className="rd-meta">
                {repoSummary.languages.map(lang => (
                  <span key={lang} className="rd-badge rd-badge--lang">{lang}</span>
                ))}
                {repoSummary.managers.map(mgr => (
                  <span key={mgr} className="rd-badge rd-badge--mgr">{mgr}</span>
                ))}
                {repoSummary.hasMonorepo && (
                  <span className="rd-badge rd-badge--mono">Monorepo</span>
                )}
                {projectInfo.length > 0 && (
                  <span className="rd-meta-text">
                    {repoSummary.projects.length} detected project{repoSummary.projects.length !== 1 ? 's' : ''}
                    {repoSummary.manifests.length > 0 && ` · manifests: ${repoSummary.manifests.join(', ')}`}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Right: actions */}
          <div className="rd-actions">
            {loading && (
              <div className="spinner-border spinner-border-sm text-secondary" role="status" style={{ flexShrink: 0 }}>
                <span className="visually-hidden">Loading…</span>
              </div>
            )}
            {repoExists ? (
              currentBranch && (
                <div className="rd-branch-group">
                  <span className="rd-branch-label">Branch</span>
                  <span className="rd-branch-value">{currentBranch}</span>
                </div>
              )
            ) : (
              <button className="rd-btn rd-btn--primary" onClick={handleCloneRepo} disabled={loading}>
                Clone Repository
              </button>
            )}
          </div>

        </div>
      </div>

      {/* ── Analysis tabs ── */}
      <RepoDetailsTabs repoName={repoName} key={refreshKey} />
    </div>
  );
}

export default RepoDetails;
